import React, { PropsWithChildren, useState, ReactNode } from 'react'
import uniqueId from 'lodash.uniqueid'
import { Tooltip as TooltipStyles } from '@airbus/elements/elements'
import tokens from '@airbus/design-tokens'
import clsx from 'clsx'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { PopoverPlacement } from '../helperTypes'
import Popper from '../Popper'
import { CommonProps } from '../helperTypes'

export enum TooltipDelay {
  NONE = tokens.time.delay.none,
  MEDIUM = tokens.time.delay.medium,
  LONG = tokens.time.delay.long,
}

type DeprecatedProps = {
  arrow?: boolean
  maxWidth?: string
}

type Props = PropsWithChildren<{
  /** Tooltip arrow className */
  arrowClassName?: string
  /** Tooltip arrow size */
  arrowSize?: number
  /** Tooltip className */
  className?: string
  /** disable tooltip */
  disabled?: boolean
  /**
   * Delay in ms before showing the tooltip
   * @default TooltipDelay.MEDIUM
   */
  enterDelay?: TooltipDelay
  /**
   * Delay in ms before hiding the tooltip
   * @default TooltipDelay.NONE
   */
  exitDelay?: TooltipDelay
  /** Tooltip label/content */
  label?: ReactNode
  /** Large variant */
  large?: boolean
  /** Callback fired when the tooltip opens */
  onOpen?: (event: Event) => void
  /** Callback fired when the tooltip closes */
  onClose?: (event: Event) => void
  /** Tooltip placement */
  placement?: PopoverPlacement
  /** Target element className */
  targetClassName?: string
  /** Large tooltip title */
  title?: string
}>

export type TooltipProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: TooltipProps) => {
  const { arrow, maxWidth, ...rest } = props
  return rest
}

const useStyles = createUseStyles(TooltipStyles, 'tooltip')

export const Tooltip = (props: TooltipProps) => {
  const {
    arrowClassName,
    arrowSize = 4,
    children,
    className,
    disabled = false,
    label,
    placement,
    large,
    onClose,
    onOpen,
    targetClassName,
    title,
    enterDelay = TooltipDelay.MEDIUM,
    exitDelay = TooltipDelay.NONE,
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps('Tooltip', { arrow: undefined, maxWidth: undefined }, props)

  const [rootNode, setRootNode] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState(false)
  const classes = useStyles()
  const id = React.useMemo(() => props.id || uniqueId('tooltip-'), [props.id])
  const enterTimer = React.useRef<ReturnType<typeof setTimeout>>()
  const exitTimer = React.useRef<ReturnType<typeof setTimeout>>()

  const toggleOpen = (open: boolean, event: any) => {
    setOpen(open)
    if (open && onOpen) onOpen(event)
    if (!open && onClose) onClose(event)
  }

  const handleEnter = (event: any) => {
    clearTimeout(enterTimer.current)
    clearTimeout(exitTimer.current)
    event.stopPropagation()

    if (open) return
    if (enterDelay) {
      enterTimer.current = setTimeout(() => toggleOpen(true, event), enterDelay)
    } else {
      toggleOpen(true, event)
    }
  }

  const handleLeave = (event: any) => {
    clearTimeout(enterTimer.current)
    clearTimeout(exitTimer.current)
    event.stopPropagation()

    if (!open) return
    if (exitDelay) {
      exitTimer.current = setTimeout(() => toggleOpen(false, event), exitDelay)
    } else {
      toggleOpen(false, event)
    }
  }

  const targetProps = {
    onMouseOver: handleEnter,
    onMouseLeave: handleLeave,
    onTouchStart: handleEnter,
    onTouchEnd: handleLeave,
    onTouchCancel: handleLeave,
    className: targetClassName,
  }

  if (!React.isValidElement(children)) {
    return null
  }

  return (
    <>
      <div ref={(node) => setRootNode(node)} aria-describedby={open ? id : undefined} {...targetProps}>
        {children}
      </div>
      {!disabled && (
        <Popper
          id={id}
          disableRoles
          role='tooltip'
          className={clsx(classes.root, large && classes._large, className)}
          arrowClassName={clsx(classes.arrow, arrowClassName)}
          arrowSize={arrowSize}
          referenceNode={rootNode}
          open={open}
          placement={placement}
          {...rest}
        >
          {title && <div className={classes.title}>{title}</div>}
          {label}
        </Popper>
      )}
    </>
  )
}

export default Tooltip
